/** The channel fields the derived screens actually display or key off. */
interface ChannelIdentity {
  id: string;
  name?: string;
  pypeId?: string;
  env?: string;
}

/**
 * PSTUDIO-4253 — whether a channel update changed anything the project-wide
 * derived caches depend on.
 *
 * `invalidateChannelDerivedCaches` nulls six slices (`defaultMessagesPypes`,
 * `liveAgentPypes`, `pypeConnections`, `apiCredentials`, `pypeRuleGroups` /
 * `pypeRuleGroupChannels`, `channelSettingsLoadFailures`) so the screens that
 * derive from the project's channels re-load. Every one of those screens keys
 * off the same three things: the channel's NAME, its PYPE and its ENV. A style
 * sheet is none of them.
 *
 * `updateChannel` invalidated unconditionally, so a CSS-only save discarded all
 * six and the next Channel Settings visit re-ran the whole load chain — the
 * chain this ticket is about. Narrowing it to the fields that matter is what
 * stops a styling edit costing a project-wide reload.
 *
 * Deliberately fails SAFE. Under-invalidating is the expensive mistake: it
 * brings back the stale-list bug the invalidation was written for (a renamed
 * channel still showing its old name on Default Messages until F5). So anything
 * that cannot be PROVEN unchanged — an unknown channel, an unloaded list, a
 * payload with no id — invalidates.
 *
 * A field the payload does not carry is "not changed", not "changed to empty":
 * `ChannelMutationProps` is shared with create, where these are optional.
 */
export const channelUpdateNeedsCacheInvalidation = ({
  channels,
  updated,
}: {
  channels: ChannelIdentity[];
  updated: ChannelIdentity;
}): boolean => {
  if (!updated.id) {
    return true;
  }

  const previous = channels.find((channel) => channel.id === updated.id);

  if (!previous) {
    return true;
  }

  const changed = (next: string | undefined, before: string | undefined) =>
    next !== undefined && next !== before;

  return (
    changed(updated.name, previous.name) ||
    changed(updated.pypeId, previous.pypeId) ||
    changed(updated.env, previous.env)
  );
};
